import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { type AlmanacCableConsumer, type AlmanacCableSubscription } from 'providers/CableConsumer/types'

export interface DesignPreviewUpdate {
  op: 'preview_update'
  image_url: string
  last_rendered_version: number
  is_generating: boolean
  session_uuid: string
  duration: number | null
}

export const useDesignPreviewSync = (eventsData: AlmanacFileCalendarItem[]) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const subscriptionsRef = useRef<Map<string, AlmanacCableSubscription>>(new Map())
  const activeConnectionRef = useRef<AlmanacCableConsumer | null>(null)

  const serializedDesignKeys = useMemo(() => {
    const designKeys = new Set<string>()
    eventsData.forEach((event) => {
      if (event.asset_type === 'Design' && (event.image_url_generating || !event.image_url) && event.key) {
        designKeys.add(event.key)
      }
    })

    return JSON.stringify(Array.from(designKeys).sort())
  }, [eventsData])

  const unsubscribeAll = useCallback(() => {
    subscriptionsRef.current.forEach((subscription) => {
      subscription.unsubscribe()
    })
    subscriptionsRef.current.clear()
    activeConnectionRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      unsubscribeAll()
    }
  }, [unsubscribeAll])

  useEffect(() => {
    if (!auth || !websocketConnection) {
      unsubscribeAll()
      return
    }

    if (activeConnectionRef.current && activeConnectionRef.current !== websocketConnection) {
      unsubscribeAll()
    }

    activeConnectionRef.current = websocketConnection

    const designKeys = JSON.parse(serializedDesignKeys) as string[]
    const activeDesignKeys = new Set(designKeys)
    const subscriptions = subscriptionsRef.current

    subscriptions.forEach((subscription, key) => {
      if (activeDesignKeys.has(key)) return

      subscription.unsubscribe()
      subscriptions.delete(key)
    })

    designKeys.forEach((key) => {
      if (subscriptions.has(key)) return

      const subscription = websocketConnection.subscriptions.create(
        {
          channel: 'DesignPreviewChannel',
          key,
        },
        {
          received(data: DesignPreviewUpdate) {
            if (data.op === 'preview_update') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, key, (f) => ({
                ...f,
                image_url: data.image_url,
                image_url_generating: false,
                duration: data.duration || f.duration,
                is_generating: false,
              }))

              updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, key, (file) => ({
                ...file,
                image_url: data.image_url,
                image_url_generating: false,
                duration: data.duration || file.duration,
                is_generating: false,
              }))
            }
          },
        },
      )

      subscriptions.set(key, subscription)
    })
  }, [auth, websocketConnection, serializedDesignKeys, queryClient, workspaceId, unsubscribeAll])
}
